import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth.hook";
import { PageLoader } from "@/components/layout/PageLoader";

function isAuthInitializing(
  loading: boolean,
  firebaseUser: unknown,
  profileLoading: boolean,
) {
  return loading || Boolean(firebaseUser && profileLoading);
}

/**
 * App routes that require a signed-in Firebase user.
 * Unauthenticated visitors are sent to /signin once auth has settled —
 * they must not see the empty dashboard shell.
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { firebaseUser, userProfile, loading, profileLoading, showStartupOnboarding } =
    useAuth();
  const location = useLocation();

  if (isAuthInitializing(loading, firebaseUser, profileLoading)) {
    return <PageLoader />;
  }

  if (!firebaseUser) {
    return <Navigate to="/signin" replace />;
  }

  if (showStartupOnboarding && location.pathname !== "/signup") {
    return <Navigate to="/signup" replace />;
  }

  if (!userProfile && location.pathname !== "/signup") {
    return <Navigate to="/signup" replace />;
  }

  return <>{children}</>;
}

/**
 * Marketing / auth pages. Logged-in users with a profile go to /dashboard.
 * Do not redirect until auth (and profile, when a Firebase session exists)
 * has settled — otherwise visitors can be dumped on a black /dashboard.
 */
export function PublicRoute({
  children,
}: {
  children: React.ReactNode;
  /** Landing page flag kept for callers; public routes no longer fork on it. */
  home?: boolean;
}) {
  const { firebaseUser, userProfile, loading, profileLoading, showStartupOnboarding } =
    useAuth();

  if (isAuthInitializing(loading, firebaseUser, profileLoading)) {
    return <>{children}</>;
  }

  const fullyAuthenticated = Boolean(
    firebaseUser && userProfile && !showStartupOnboarding,
  );

  if (fullyAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
